import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformConfig, useBatchUpdatePlatformConfig, PlatformConfigValues, defaultConfigValues } from "@/hooks/usePlatformConfig";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Settings, Building2, Palette, Shield, Loader2, Plus, X, Save } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDistanceToNow } from "date-fns";

const PLAN_OPTIONS = ["starter", "pro", "enterprise"] as const;

export default function PlatformSettingsPage() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { data: config, isLoading, error } = usePlatformConfig();
  const batchUpdate = useBatchUpdatePlatformConfig();

  // Form state
  const [formValues, setFormValues] = useState<PlatformConfigValues>(defaultConfigValues);
  const [isDirty, setIsDirty] = useState(false);
  const [newFont, setNewFont] = useState("");

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && (!user || !isAdmin())) {
      navigate("/");
    }
  }, [user, authLoading, isAdmin, navigate]);

  // Initialize form when config loads
  useEffect(() => {
    if (config) {
      const values: PlatformConfigValues = {
        max_tenants_per_agency: config.max_tenants_per_agency?.value ?? defaultConfigValues.max_tenants_per_agency,
        tenant_creation_enabled: config.tenant_creation_enabled?.value ?? defaultConfigValues.tenant_creation_enabled,
        require_tenant_verification: config.require_tenant_verification?.value ?? defaultConfigValues.require_tenant_verification,
        allowed_tenant_plans: config.allowed_tenant_plans?.value ?? defaultConfigValues.allowed_tenant_plans,
        branding_enabled: config.branding_enabled?.value ?? defaultConfigValues.branding_enabled,
        custom_domains_enabled: config.custom_domains_enabled?.value ?? defaultConfigValues.custom_domains_enabled,
        max_logo_size_kb: config.max_logo_size_kb?.value ?? defaultConfigValues.max_logo_size_kb,
        allowed_fonts: config.allowed_fonts?.value ?? defaultConfigValues.allowed_fonts,
        require_powered_by: config.require_powered_by?.value ?? defaultConfigValues.require_powered_by,
        allow_impersonation: config.allow_impersonation?.value ?? defaultConfigValues.allow_impersonation,
        impersonation_max_duration_minutes: config.impersonation_max_duration_minutes?.value ?? defaultConfigValues.impersonation_max_duration_minutes,
        session_timeout_minutes: config.session_timeout_minutes?.value ?? defaultConfigValues.session_timeout_minutes,
      };
      setFormValues(values);
      setIsDirty(false);
    }
  }, [config]);

  const updateValue = <K extends keyof PlatformConfigValues>(
    key: K,
    value: PlatformConfigValues[K]
  ) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    // Only send changed values
    const changes: Partial<PlatformConfigValues> = {};
    for (const key of Object.keys(formValues) as (keyof PlatformConfigValues)[]) {
      const currentValue = config?.[key]?.value;
      const newValue = formValues[key];
      if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
        (changes as Record<string, unknown>)[key] = newValue;
      }
    }

    if (Object.keys(changes).length === 0) {
      setIsDirty(false);
      return;
    }

    await batchUpdate.mutateAsync(changes);
    setIsDirty(false);
  };

  const getMetadata = (key: keyof PlatformConfigValues) => {
    const item = config?.[key];
    if (!item?.updated_at) return null;
    return {
      updated_at: item.updated_at,
      updated_by: item.updated_by,
    };
  };

  const formatMetadata = (key: keyof PlatformConfigValues) => {
    const meta = getMetadata(key);
    if (!meta) return null;
    const timeAgo = formatDistanceToNow(new Date(meta.updated_at), { addSuffix: true });
    return `Last updated ${timeAgo}${meta.updated_by ? ` by ${meta.updated_by}` : ""}`;
  };

  const addFont = () => {
    if (newFont.trim() && !formValues.allowed_fonts.includes(newFont.trim())) {
      updateValue("allowed_fonts", [...formValues.allowed_fonts, newFont.trim()]);
      setNewFont("");
    }
  };

  const removeFont = (font: string) => {
    updateValue("allowed_fonts", formValues.allowed_fonts.filter((f) => f !== font));
  };

  const togglePlan = (plan: string) => {
    const current = formValues.allowed_tenant_plans;
    if (current.includes(plan)) {
      updateValue("allowed_tenant_plans", current.filter((p) => p !== plan));
    } else {
      updateValue("allowed_tenant_plans", [...current, plan]);
    }
  };

  if (authLoading || isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto p-4 md:p-6 space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="container mx-auto p-4 md:p-6">
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Settings</CardTitle>
              <CardDescription>{error.message}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Settings className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Platform Settings</h1>
              <p className="text-muted-foreground text-sm">
                Configure global platform behavior and defaults
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={!isDirty || batchUpdate.isPending}
            className="gap-2"
          >
            {batchUpdate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="grid gap-6">
          {/* Tenant Creation Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Tenant Creation</CardTitle>
              </div>
              <CardDescription>
                Control how agencies can create and manage tenants
              </CardDescription>
              {formatMetadata("tenant_creation_enabled") && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatMetadata("tenant_creation_enabled")}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="max_tenants">Maximum Tenants per Agency</Label>
                  <Input
                    id="max_tenants"
                    type="number"
                    min={1}
                    max={10000}
                    value={formValues.max_tenants_per_agency}
                    onChange={(e) => updateValue("max_tenants_per_agency", parseInt(e.target.value) || 100)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Tenant Creation Enabled</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow agencies to create new tenants
                    </p>
                  </div>
                  <Switch
                    checked={formValues.tenant_creation_enabled}
                    onCheckedChange={(checked) => updateValue("tenant_creation_enabled", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Require Tenant Verification</Label>
                    <p className="text-sm text-muted-foreground">
                      New tenants must be verified before activation
                    </p>
                  </div>
                  <Switch
                    checked={formValues.require_tenant_verification}
                    onCheckedChange={(checked) => updateValue("require_tenant_verification", checked)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label>Allowed Tenant Plans</Label>
                <div className="flex flex-wrap gap-3">
                  {PLAN_OPTIONS.map((plan) => (
                    <div key={plan} className="flex items-center gap-2">
                      <Checkbox
                        id={`plan-${plan}`}
                        checked={formValues.allowed_tenant_plans.includes(plan)}
                        onCheckedChange={() => togglePlan(plan)}
                      />
                      <Label htmlFor={`plan-${plan}`} className="text-sm capitalize cursor-pointer">
                        {plan}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Branding</CardTitle>
              </div>
              <CardDescription>
                Manage branding capabilities and constraints
              </CardDescription>
              {formatMetadata("branding_enabled") && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatMetadata("branding_enabled")}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Branding Enabled</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow agencies to customize branding
                    </p>
                  </div>
                  <Switch
                    checked={formValues.branding_enabled}
                    onCheckedChange={(checked) => updateValue("branding_enabled", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Custom Domains Enabled</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow agencies to use custom domains
                    </p>
                  </div>
                  <Switch
                    checked={formValues.custom_domains_enabled}
                    onCheckedChange={(checked) => updateValue("custom_domains_enabled", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Require "Powered By" Badge</Label>
                    <p className="text-sm text-muted-foreground">
                      Require Calie.AI attribution on white-label sites
                    </p>
                  </div>
                  <Switch
                    checked={formValues.require_powered_by}
                    onCheckedChange={(checked) => updateValue("require_powered_by", checked)}
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="max_logo">Maximum Logo Size (KB)</Label>
                  <Input
                    id="max_logo"
                    type="number"
                    min={64}
                    max={5120}
                    value={formValues.max_logo_size_kb}
                    onChange={(e) => updateValue("max_logo_size_kb", parseInt(e.target.value) || 512)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label>Allowed Fonts</Label>
                <div className="flex flex-wrap gap-2">
                  {formValues.allowed_fonts.map((font) => (
                    <Badge key={font} variant="secondary" className="gap-1 pr-1">
                      {font}
                      <button
                        onClick={() => removeFont(font)}
                        className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add font family..."
                    value={newFont}
                    onChange={(e) => setNewFont(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFont())}
                    className="max-w-xs"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={addFont}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Security</CardTitle>
              </div>
              <CardDescription>
                Configure security policies and session management
              </CardDescription>
              {formatMetadata("allow_impersonation") && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatMetadata("allow_impersonation")}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Allow Impersonation</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow platform admins to impersonate tenant users
                  </p>
                </div>
                <Switch
                  checked={formValues.allow_impersonation}
                  onCheckedChange={(checked) => updateValue("allow_impersonation", checked)}
                />
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="impersonation_duration">
                    Impersonation Max Duration (minutes)
                  </Label>
                  <Input
                    id="impersonation_duration"
                    type="number"
                    min={5}
                    max={1440}
                    value={formValues.impersonation_max_duration_minutes}
                    onChange={(e) =>
                      updateValue("impersonation_max_duration_minutes", parseInt(e.target.value) || 60)
                    }
                    disabled={!formValues.allow_impersonation}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="session_timeout">Session Timeout (minutes)</Label>
                  <Input
                    id="session_timeout"
                    type="number"
                    min={15}
                    max={10080}
                    value={formValues.session_timeout_minutes}
                    onChange={(e) =>
                      updateValue("session_timeout_minutes", parseInt(e.target.value) || 480)
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Users will be logged out after this period of inactivity
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Floating Save Button for Mobile */}
        {isDirty && (
          <div className="fixed bottom-4 right-4 md:hidden">
            <Button
              onClick={handleSave}
              disabled={batchUpdate.isPending}
              size="lg"
              className="shadow-lg gap-2"
            >
              {batchUpdate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
